import Footer from 'app/components/FooterComponent';
import Header from 'app/components/HeaderComponent';
import HeaderImage from 'home/components/HeaderImage';
import Loader from '__common/components/LoaderComponent';
import React from 'react';
import { Alert, Box, Button, Divider, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  CHANGE_PASSWORD_ON_DEMAND,
  RESET_PASSWORD_ON_DEMAND,
  SET_EMAIL_PAGE,
  SET_PASSWORD_PAGE,
  RESET_PASSWORD_CLEAR,
  } from 'actions';

type State = {
  email: string;
  newPassword: string;
  newPasswordRepeat: string;
  passwordNotMatch: boolean,
  touched: boolean,
  showPassword: boolean,
  showPasswordRepeat: boolean,
};

type Props = {
  dispatch: Function;
  forgotPassword: forgotPasswordState;
  match: any;
};

class ForgotPassword extends React.Component<Props, State> {
  state = {
    email: '',
    newPassword: '',
    newPasswordRepeat: '',
    passwordNotMatch: false,
    touched: false,
    showPassword: false,
    showPasswordRepeat: false,
  };

  componentDidMount() {
    this._setPage();
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(RESET_PASSWORD_CLEAR());
  }

  _setPage() {
    const {
      dispatch,
      match: {
        params: { token, uid },
      },
    } = this.props;

    if (token && token.length && uid && uid.length) {
      dispatch(SET_PASSWORD_PAGE());
    } else {
      dispatch(SET_EMAIL_PAGE());
    }
  }

  isEmailValid = () => /^\S+@\S+\.\S+$/.test(this.state.email.trim())

  _setEmail = (email: string) => {
    this.setState({ email });
  }

  _setNewPassword = (newPassword: string) => {
    this.setState({ newPassword, passwordNotMatch: false });
  }

  _setNewPasswordRepeat = (newPasswordRepeat: string) => {
    this.setState({ newPasswordRepeat, passwordNotMatch: false });
  }

  _resetPasswordHandler = () => {
    const { email } = this.state;
    const { dispatch } = this.props;
    this.setState({ touched: true });

    if (email && this.isEmailValid()) {
      dispatch(RESET_PASSWORD_ON_DEMAND(email.trim()));
    }
  }

  _changePasswordHandler = () => {
    const { 
      match: {
        params: { token, uid },
    }, 
  } = this.props;
    const { newPassword, newPasswordRepeat } = this.state;
    const { dispatch } = this.props;
    this.setState({ touched: true });

    if (newPassword.length < 8 || newPasswordRepeat.length === 0) {
      return;
    }

    if (newPassword !== newPasswordRepeat) {
      return this.setState({ passwordNotMatch: true });
    }

    this.setState({ passwordNotMatch: false });
    dispatch(CHANGE_PASSWORD_ON_DEMAND(newPassword, newPasswordRepeat, token, uid));
  }

  handleKeyPressOnFormInput = (e, cb) => {
    if (e.key === 'Enter') {
      cb();
    }
  }

  changePassword = () => {
    const { newPassword, newPasswordRepeat, passwordNotMatch, touched, showPassword, showPasswordRepeat } = this.state;
    const { forgotPassword: { sending } } = this.props;
    const passwordError = touched && newPassword.length < 8;
    const repeatError = passwordNotMatch || (touched && newPasswordRepeat.length > 0 && newPassword !== newPasswordRepeat);

    return (
      <Box className="auth-card">
        <Typography variant="h4" className="glass-title">Create New Password</Typography>
        <Typography variant="body2" className="glass-subtitle">Choose a new password for your U-Builder account.</Typography>
        <Divider className="glass-divider" sx={{ my: 3 }} />
        <Box component="form" className="auth-form" noValidate>
          <TextField
            id="newpassword"
            type={showPassword ? 'text' : 'password'}
            label="New Password"
            placeholder="New password"
            fullWidth
            error={passwordError}
            helperText={passwordError ? 'Password must be at least 8 characters.' : 'Use at least 8 characters.'}
            onKeyDown={e => this.handleKeyPressOnFormInput(e, this._changePasswordHandler)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this._setNewPassword(event.target.value)}
            value={newPassword}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LockIcon /></InputAdornment>,
              endAdornment: <InputAdornment position="end"><IconButton edge="end" onClick={() => this.setState({ showPassword: !showPassword })} aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton></InputAdornment>,
            }}
          />
          <TextField
            id="newpasswordrepeat"
            type={showPasswordRepeat ? 'text' : 'password'}
            label="Repeat New Password"
            placeholder="Repeat your password"
            fullWidth
            error={repeatError}
            helperText={repeatError ? 'Passwords do not match.' : ' '}
            onKeyDown={e => this.handleKeyPressOnFormInput(e, this._changePasswordHandler)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this._setNewPasswordRepeat(event.target.value)}
            value={newPasswordRepeat}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LockIcon /></InputAdornment>,
              endAdornment: <InputAdornment position="end"><IconButton edge="end" onClick={() => this.setState({ showPasswordRepeat: !showPasswordRepeat })} aria-label={showPasswordRepeat ? 'Hide repeated password' : 'Show repeated password'}>{showPasswordRepeat ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton></InputAdornment>,
            }}
          />
        </Box>
        <Button variant="contained" type="button" className="form_button" id="reset_password_button" fullWidth disabled={sending} onClick={this._changePasswordHandler}>
          {sending ? 'Changing password...' : 'Change Password'}
        </Button>
      </Box>
    );
  }

  emailForm = () => {
    const { email, touched } = this.state;
    const { forgotPassword: { sending } } = this.props;
    const emailError = touched && !this.isEmailValid();

    return (
      <Box className="auth-card">
        <Typography variant="h4" className="glass-title">Reset Password</Typography>
        <Typography variant="body2" className="glass-subtitle">Enter your email and we will send reset instructions.</Typography>
        <Divider className="glass-divider" sx={{ my: 3 }} />
        <Box component="form" className="auth-form" noValidate>
          <TextField
            id="email"
            type="email"
            label="Email Address"
            placeholder="your.email@example.com"
            fullWidth
            error={emailError}
            helperText={emailError ? 'Enter a valid email address.' : ' '}
            onKeyDown={e => this.handleKeyPressOnFormInput(e, this._resetPasswordHandler)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this._setEmail(event.target.value)}
            value={email}
            InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment> }}
          />
        </Box>
        <Button variant="contained" type="button" className="form_button" id="reset_password_button" fullWidth disabled={sending} onClick={this._resetPasswordHandler}>
          {sending ? 'Sending instructions...' : 'Send Reset Instructions'}
        </Button>
        <Box className="glass-links"><Link to="/login" className="glass-link glass-link-primary">Back to sign in</Link></Box>
      </Box>
    );
  }

  stateCard = (severity: 'success' | 'error', title: string, message: string) => {
    return (
      <Box className="auth-card">
        <Typography variant="h4" className="glass-title">{title}</Typography>
        <Divider className="glass-divider" sx={{ my: 3 }} />
        <Alert severity={severity} className="auth-alert">{message}</Alert>
        <Box className="glass-links"><Link to="/login" className="glass-link glass-link-primary">Back to sign in</Link></Box>
      </Box>
    );
  }

  content = () => {
    const {
      forgotPassword: { errorEmail, sending, successEmail, changeEmail, changePassword, errorPassword, successPassword },
    } = this.props;

    if (sending) {
      return <Loader />;
    }

    if (errorEmail) {
      return this.stateCard('error', 'Reset Password', 'We could not send reset instructions. Check the email and try again.');
    }

    if (successEmail) {
      return this.stateCard('success', 'Check Your Email', 'We sent password reset instructions to your email address.');
    }

    if (errorPassword) {
      return this.stateCard('error', 'Reset Password', 'We could not change your password. The reset link may be invalid or expired.');
    }

    if (successPassword) {
      return this.stateCard('success', 'Password Changed', 'Your password has been changed. You can now sign in.');
    }

    if (changeEmail) {
      return this.emailForm();
    }

    if (changePassword) {
      return this.changePassword();
    }

    return this.emailForm();
  }

  render() {
    return (
      <div className="app-content forgot-password glass-login-page">
        <Header />
        <HeaderImage content={this.content()} />
        <Footer />
      </div>
    );
  }
}

function mapStateToProps(state: appState) {
  return {
    forgotPassword: state.forgotPassword,
  };
}

export default connect(mapStateToProps)(ForgotPassword);
